import React, { useState, useEffect, useCallback } from 'react';
import { TransformationStrategy, PriorityLevel, GrowthLevel } from '../../types';
import type { AOPItemCreateRequest } from '../../types';
import ArtOfPossibleTable from './ArtOfPossibleTable';
import { HelpButton } from '../common/HelpButton';
import { useCustomer } from '../../context/CustomerContext';
import { toast } from 'react-toastify';
import { aopApi } from '../../services/api';

const AOP_ID_PLAN_ID_SEPARATOR = '|';

/** Parse strategy id as "aop_id|plan_id"; returns [aop_id, plan_id] or null */
function parseStrategyId(id: string): [string, string] | null {
  const idx = id.indexOf(AOP_ID_PLAN_ID_SEPARATOR);
  if (idx <= 0 || idx === id.length - 1) return null;
  const aopId = id.slice(0, idx);
  const planId = id.slice(idx + 1);
  return aopId && planId ? [aopId, planId] : null;
}

interface ArtOfPossibleTabProps {
  onShowHelp?: () => void;
}

const ArtOfPossibleTab: React.FC<ArtOfPossibleTabProps> = ({ onShowHelp }) => {
  const { customerData, updateCustomerData } = useCustomer();
  const [strategies, setStrategies] = useState<TransformationStrategy[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  /** Strategy ids modified since last load/save; only these are sent to the API */
  const [, setDirtyStrategyIds] = useState<Set<string>>(new Set());

  const fetchAopData = useCallback(async () => {
    const customerId = customerData?.customer_id;
    if (!customerId) {
      setStrategies([]);
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await aopApi.getCustomerAop(customerId);
      const strategiesList: TransformationStrategy[] = [];
      (response.aop_items || []).forEach((item) => {
        (item.plans || []).forEach((plan, index) => {
          strategiesList.push({
            id: `${item.aop_id}${AOP_ID_PLAN_ID_SEPARATOR}${plan.plan_id}`,
            transformationArea: item.label,
            notes: plan.name ?? '',
            priority: (plan.business_impact as PriorityLevel) ?? 'Select',
            growth: (plan.technical_impact as GrowthLevel) ?? 'Select',
            rank: plan.rank ?? index + 1,
            aop_id: item.aop_id,
            plan_id: plan.plan_id,
          });
        });
      });
      strategiesList.sort((a, b) => {
        if (a.transformationArea !== b.transformationArea) {
          return a.transformationArea.localeCompare(b.transformationArea);
        }
        return a.rank - b.rank;
      });
      setStrategies(strategiesList);
      setDirtyStrategyIds(new Set());
    } catch (err) {
      setError('Failed to load Art of Possible data. Please try again.');
      console.error('Error fetching AOP:', err);
      setStrategies([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerData?.customer_id]);

  useEffect(() => {
    fetchAopData();
  }, [fetchAopData]);

  /**
   * Build POST body as array of AOPItemCreateRequest.
   * Returns null if any strategy is missing aop_id or plan_id (caller should guard and show error).
   */
  const buildAopPayload = (strategiesToConvert: TransformationStrategy[]): AOPItemCreateRequest[] | null => {
    const items: AOPItemCreateRequest[] = [];
    for (const strategy of strategiesToConvert) {
      const aopId = strategy.aop_id ?? parseStrategyId(strategy.id)?.[0];
      const planId = strategy.plan_id ?? parseStrategyId(strategy.id)?.[1];
      if (!aopId?.trim() || !planId?.trim()) {
        return null;
      }
      items.push({
        aop_id: aopId,
        label: strategy.transformationArea,
        plan_id: planId,
        data: {
          name: strategy.notes,
          business_impact: strategy.priority === 'Select' ? '' : strategy.priority,
          technical_impact: strategy.growth === 'Select' ? '' : strategy.growth,
          rank: strategy.rank,
        },
      });
    }
    return items;
  };

  const updateAopData = async (
    updatedStrategies: TransformationStrategy[],
    idsToSend?: Set<string>
  ) => {
    if (!customerData?.customer_id) {
      return;
    }
    const toSend =
      idsToSend && idsToSend.size > 0
        ? updatedStrategies.filter((s) => idsToSend.has(s.id))
        : updatedStrategies;
    const payload = buildAopPayload(toSend);
    if (!payload || payload.length === 0) {
      if (!idsToSend || idsToSend.size === 0) return;
      toast.error('Cannot update: Art of Possible row is missing aop_id or plan_id.');
      return;
    }
    try {
      await aopApi.createOrUpdateCustomerAop(customerData.customer_id, payload);
      setDirtyStrategyIds((prev) => {
        if (!idsToSend) return prev;
        const next = new Set(prev);
        idsToSend.forEach((id) => next.delete(id));
        return next;
      });
      const byArea = updatedStrategies.reduce<Record<string, Array<{ name: string; rank: number; growth: string; priority: string }>>>(
        (acc, s) => {
          const area = s.transformationArea;
          if (!acc[area]) acc[area] = [];
          acc[area].push({
            name: s.notes,
            rank: s.rank,
            growth: s.growth === 'Select' ? '' : s.growth,
            priority: s.priority === 'Select' ? '' : s.priority,
          });
          return acc;
        },
        {}
      );
      const aopsArray = Object.entries(byArea).map(([area, notes]) => ({ area, notes }));
      updateCustomerData({ art_of_possible: { aops: aopsArray } });
      toast.success('Art of Possible data updated successfully!');
    } catch (err) {
      console.error('Error updating AOP data:', err);
      throw err;
    }
  };

  const handlePriorityChange = async (id: string, value: PriorityLevel) => {
    try {
      const updatedStrategies = strategies.map((strategy) =>
        strategy.id === id ? { ...strategy, priority: value } : strategy
      );
      setStrategies(updatedStrategies);
      await updateAopData(updatedStrategies, new Set([id]));
    } catch (err) {
      setError('Failed to update priority. Please try again.');
      fetchAopData();
    }
  };

  const handleGrowthChange = async (id: string, value: GrowthLevel) => {
    try {
      const updatedStrategies = strategies.map((strategy) =>
        strategy.id === id ? { ...strategy, growth: value } : strategy
      );
      setStrategies(updatedStrategies);
      await updateAopData(updatedStrategies, new Set([id]));
    } catch (err) {
      setError('Failed to update growth. Please try again.');
      fetchAopData();
    }
  };

  const handleRankChange = async (transformationArea: string, sourceIndex: number, destinationIndex: number) => {
    try {
      const areaStrategies = strategies.filter((s) => s.transformationArea === transformationArea);
      const areaIds = new Set(areaStrategies.map((s) => s.id));
      const otherStrategies = strategies.filter((s) => s.transformationArea !== transformationArea);
      const sourceStrategy = areaStrategies[sourceIndex];
      const reordered = areaStrategies.filter((_, i) => i !== sourceIndex);
      reordered.splice(destinationIndex, 0, sourceStrategy);
      const reorderedWithRanks = reordered.map((s, index) => ({ ...s, rank: index + 1 }));
      const finalStrategies = [...otherStrategies, ...reorderedWithRanks].sort((a, b) => {
        if (a.transformationArea !== b.transformationArea) {
          return a.transformationArea.localeCompare(b.transformationArea);
        }
        return a.rank - b.rank;
      });
      setStrategies(finalStrategies);
      await updateAopData(finalStrategies, areaIds);
    } catch (err) {
      setError('Failed to update ranks. Please try again.');
      fetchAopData();
    }
  };

  return (
    <div className="px-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3 items-center">
          <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Art Of Possible</h1>
          {onShowHelp && <HelpButton onClick={onShowHelp} />}
        </div>
      </div>

      {!customerData?.customer_id ? (
        <div className="flex justify-center py-10 items-center h-full min-h-[400px]">
          <p className="text-gray-500">Select a customer to view and edit Art of Possible.</p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-10 items-center h-full min-h-[400px]">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]" />
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
          <button type="button" onClick={fetchAopData} className="underline text-sm mt-2">
            Try again
          </button>
        </div>
      ) : (
        <ArtOfPossibleTable
          strategies={strategies}
          onPriorityChange={handlePriorityChange}
          onGrowthChange={handleGrowthChange}
          onRankChange={handleRankChange}
        />
      )}
    </div>
  );
};

export default ArtOfPossibleTab;
